"use client";

import { useRouter } from "next/navigation";
import { useEffect, useLayoutEffect, useRef } from "react";
import {
  playSectionShift,
  releaseHeldAnimations,
  snapshotSections,
} from "./monter-animation";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const POLL_INTERVAL = 15_000;

// Sätts när bevakaren uppdaterar sidan, så att MonterReveal vet att montern
// dyker upp under besöket och ska animeras in (men inte vid vanlig sidladdning).
// En tidsgräns i stället för en flagga som förbrukas, eftersom React kan
// rendera komponenten flera gånger (t.ex. i strict mode).
const REVEAL_WINDOW = 15_000;
let revealUntil = 0;

export const shouldRevealMonter = () => Date.now() < revealUntil;

// MonterReveal registrerar en funktion per sektion som animerar ut montern.
// Bevakaren kör den innan sidan uppdateras, eftersom React annars tar bort
// montern direkt när servern slutar rendera den.
const hideHandlers = new Map<string, () => Promise<void>>();

export const registerMonterHide = (
  sectionId: string,
  hide: () => Promise<void>,
) => {
  hideHandlers.set(sectionId, hide);
  return () => {
    if (hideHandlers.get(sectionId) === hide) hideHandlers.delete(sectionId);
  };
};

// Nyckeln har formatet "sektionId:avsnittId,sektionId:avsnittId"
const parseKey = (key: string) =>
  new Map(
    key
      .split(",")
      .filter(Boolean)
      .map((entry) => entry.split(":") as [string, string]),
  );

// Sektioner som visade ett avsnitt men inte gör det längre
const getRemovedSections = (oldKey: string, newKey: string) => {
  const next = parseKey(newKey);
  return Array.from(parseKey(oldKey))
    .filter(([sectionId, episodeId]) => episodeId && !next.get(sectionId))
    .map(([sectionId]) => sectionId);
};

type MonterLiveWatcherProps = {
  pageId: string;
  // Nyckel för de monter-sektioner och avsnitt som servern renderade
  currentKey: string;
};

// Pollar sidans "Monter - live"-sektioner och uppdaterar sidan utan omladdning
// när montern eller dess avsnitt publiceras eller avpubliceras mellan
// sändningarna. Jämförelsen görs mot det server-renderade läget, så om sidans
// cache ännu inte hunnit uppdateras försöker vi igen vid nästa poll.
const MonterLiveWatcher = ({ pageId, currentKey }: MonterLiveWatcherProps) => {
  const router = useRouter();
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;
  const sectionSnapshot = useRef<ReturnType<typeof snapshotSections> | null>(
    null,
  );

  // Körs när sidan uppdaterats med en ny monter, innan den ritas
  useIsomorphicLayoutEffect(() => {
    if (!sectionSnapshot.current) return;

    releaseHeldAnimations();
    playSectionShift(sectionSnapshot.current);
    sectionSnapshot.current = null;
  }, [currentKey]);

  useEffect(() => {
    let fetching = false;

    const check = async () => {
      if (fetching || document.hidden) return;
      fetching = true;

      try {
        const res = await fetch(
          `/api/monter-check?page=${encodeURIComponent(pageId)}`,
          { cache: "no-store" },
        );
        if (!res.ok) return;

        const data: { key: string } = await res.json();
        if (data.key !== currentKeyRef.current) {
          await Promise.all(
            getRemovedSections(currentKeyRef.current, data.key).map(
              (sectionId) => hideHandlers.get(sectionId)?.(),
            ),
          );

          revealUntil = Date.now() + REVEAL_WINDOW;
          sectionSnapshot.current = snapshotSections();
          router.refresh();
        }
      } catch {
        // Ignorera nätverksfel, nästa poll försöker igen
      } finally {
        fetching = false;
      }
    };

    const interval = setInterval(check, POLL_INTERVAL);
    document.addEventListener("visibilitychange", check);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
    };
  }, [pageId, router]);

  return null;
};

export default MonterLiveWatcher;
